// src/private/src/pages/app/reports/DailyPulse.jsx
import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Collapse,
  IconButton,
  CircularProgress,
  TableSortLabel
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import {
  DateRangePicker,
  DollarFormatter,
  PercentageFormatter
} from '@src/components';
import { useDailyPulse } from '@src/context';

export default function DailyPulse() {
  const {
    data,
    startDate,
    endDate,
    setDateRange,
    loadData,
    isLoading,
    orderBy,
    orderDirection,
    setSort
  } = useDailyPulse();

  const [expandedClientRow, setExpandedClientRow] = React.useState(null);

  const handleSort = (field) => setSort(field);

  const handleToggleExpand = async (row) => {
    const clientId = row.client_id || row.uuid;
    if (expandedClientRow?.client_id === clientId || expandedClientRow?.uuid === clientId) {
      setExpandedClientRow(null);
    } else {
      setExpandedClientRow(row);
      await loadData(row);
    }
  };

  const handleDateRangeChange = (e) => {
    const { startDate, endDate } = e.selection;
    setDateRange(startDate, endDate);
  };

  // ✅ Re-fetch channel data on date change if a row is expanded
  React.useEffect(() => {
    setExpandedClientRow(null);
  }, [startDate, endDate]);
  
  

  const renderSortableHeader = (label, field) => (
    <TableCell sortDirection={orderBy === field ? orderDirection : false}>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? orderDirection : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const getChannelsForClient = (clientId) => {
    const client = data.find(d => d.client_id === clientId || d.uuid === clientId);
    return client?.channels || [];
  };

  return (
    <Box p={3}>
      <Card>
        <CardHeader title="Daily Pulse Report" />
        <Divider />
        <CardContent>
          <Stack direction="row" spacing={3} mb={3} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" fontWeight={500} mb={0.5} display="block">
                Date Range
              </Typography>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                style={{ minWidth: 220 }}
              />
            </Box>
          </Stack>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  {renderSortableHeader('Client', 'client__name')}
                  {renderSortableHeader('Spend', 'total_spend_sum')}
                  {renderSortableHeader('Leads', 'total_leads_sum')}
                  {renderSortableHeader('CP Lead', 'cost_per_lead_avg')}
                  {renderSortableHeader('Accepted Rate', 'accepted_avg')}
                  {renderSortableHeader('Out Rate', 'out_of_area_avg')}
                  {renderSortableHeader('Self Books', 'self_books_sum')}
                  {renderSortableHeader('CP Self Book', 'cost_per_self_book_avg')}
                  {renderSortableHeader('Self Book Rate', 'self_book_rate_avg')}
                  {renderSortableHeader('Booked', 'booked_sum')}
                  {renderSortableHeader('Scheduled', 'scheduled_sum')}
                  {renderSortableHeader('Issues', 'issues_sum')}
                  {renderSortableHeader('Demos', 'demos_sum')}
                  {renderSortableHeader('Gross Sales', 'gross_sales_sum')}
                  {renderSortableHeader('Gross Amount', 'gross_amount_sum')}
                  {renderSortableHeader('Net Sales', 'net_sales_sum')}
                  {renderSortableHeader('Net Amount', 'net_amount_sum')}
                </TableRow>
              </TableHead>
              <TableBody>
                {data
                  .filter(d => d.client__name) // only top-level clients
                  .map((row) => {
                    const isExpanded = expandedClientRow?.client_id === row.client_id;
                    return (
                      <React.Fragment key={row.uuid}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleToggleExpand(row)}>
                              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{row.client__name}</TableCell>
                          <TableCell><DollarFormatter value={row.total_spend_sum} /></TableCell>
                          <TableCell>{row.total_leads_sum}</TableCell>
                          <TableCell><DollarFormatter value={row.cost_per_lead_avg} /></TableCell>
                          <TableCell><PercentageFormatter value={row.accepted_avg} /></TableCell>
                          <TableCell><PercentageFormatter value={row.out_of_area_avg} /></TableCell>
                          <TableCell>{row.self_books_sum}</TableCell>
                          <TableCell><DollarFormatter value={row.cost_per_self_book_avg} /></TableCell>
                          <TableCell><PercentageFormatter value={row.self_book_rate_avg} /></TableCell>
                          <TableCell>{row.booked_sum}</TableCell>
                          <TableCell>{row.scheduled_sum}</TableCell>
                          <TableCell>{row.issues_sum}</TableCell>
                          <TableCell>{row.demos_sum}</TableCell>
                          <TableCell>{row.gross_sales_sum}</TableCell>
                          <TableCell><DollarFormatter value={row.gross_amount_sum} /></TableCell>
                          <TableCell>{row.net_sales_sum}</TableCell>
                          <TableCell><DollarFormatter value={row.net_amount_sum} /></TableCell>

                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={18}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box m={2}>
                                <Typography variant="subtitle2" mb={1}>Channel Breakdown</Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Channel</TableCell>
                                      <TableCell>Spend</TableCell>
                                      <TableCell>Leads</TableCell>
                                      <TableCell>CPL</TableCell>
                                      <TableCell>Accepted Rate</TableCell>
                                      <TableCell>Out Rate</TableCell>
                                      <TableCell>Self Books</TableCell>
                                      <TableCell>CP Self Book</TableCell>
                                      <TableCell>Booked</TableCell>
                                      <TableCell>Scheduled</TableCell>
                                      <TableCell>Issues</TableCell>
                                      <TableCell>Demos</TableCell>
                                      <TableCell>Gross Sales</TableCell>
                                      <TableCell>Gross Amount</TableCell>
                                      <TableCell>Net Sales</TableCell>
                                      <TableCell>Net Amount</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {getChannelsForClient(row.client_id).map((ch) => (
                                      <TableRow key={ch.uuid}>
                                        <TableCell>{ch.channel__name}</TableCell>
                                        <TableCell><DollarFormatter value={ch.total_spend_sum} /></TableCell>
                                        <TableCell>{ch.total_leads_sum}</TableCell>
                                        <TableCell><DollarFormatter value={ch.cost_per_lead_avg} /></TableCell>
                                        <TableCell><PercentageFormatter value={ch.accepted_avg} /></TableCell>
                                        <TableCell><PercentageFormatter value={ch.out_of_area_avg} /></TableCell>
                                        <TableCell>{ch.self_books_sum}</TableCell>
                                        <TableCell><DollarFormatter value={ch.cost_per_self_book_avg} /></TableCell>
                                        <TableCell>{ch.booked_sum}</TableCell>
                                        <TableCell>{ch.scheduled_sum}</TableCell>
                                        <TableCell>{ch.issues_sum}</TableCell>
                                        <TableCell>{ch.demos_sum}</TableCell>
                                        <TableCell>{ch.gross_sales_sum}</TableCell>
                                        <TableCell><DollarFormatter value={ch.gross_amount_sum} /></TableCell>
                                        <TableCell>{ch.net_sales_sum}</TableCell>
                                        <TableCell><DollarFormatter value={ch.net_amount_sum} /></TableCell>

                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      {isLoading ? <CircularProgress size={20} /> : 'No data found.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
